import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Chip,
  Stack,
  Divider,
  TextField,
  Select,
  MenuItem,
  FormControl,
  IconButton,
  Paper,
  Avatar,
  AvatarGroup,
  LinearProgress,
  Alert,
  Grid
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  CalendarToday as CalendarIcon,
  Flag as FlagIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  Assignment as AssignmentIcon,
  History as HistoryIcon,
  AttachFile as AttachmentIcon,
  Comment as CommentIcon,
  Repeat as RepeatIcon,
  Alarm as AlarmIcon,
  Work as WorkIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { Task, UpdateTaskDTO, RecurringTask, CreateRecurringTaskDTO, UpdateRecurringTaskDTO, todoService } from '../../services/api/todo.service';
import RecurringTaskDialog from './RecurringTaskDialog';
import TaskHistoryDialog from './TaskHistoryDialog';
import ReminderListDialog from './ReminderListDialog';
import CitationsDropdown from '../Chat/CitationsDropdown';

interface TaskDetailModalProps {
  task: Task | null;
  open: boolean;
  onClose: () => void;
  onUpdate: (taskId: string, updates: UpdateTaskDTO) => void;
}

const TaskDetailModal: React.FC<TaskDetailModalProps> = ({
  task,
  open,
  onClose,
  onUpdate
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState<Partial<Task>>({});
  const [showRecurringDialog, setShowRecurringDialog] = useState(false);
  const [showHistoryDialog, setShowHistoryDialog] = useState(false);
  const [showReminderDialog, setShowReminderDialog] = useState(false);
  const [recurringTask, setRecurringTask] = useState<RecurringTask | null>(null);

  useEffect(() => {
    if (task) {
      setEditForm({
        title: task.title,
        description: task.description || '',
        expected_outcome: (task as any).expected_outcome || '',
        status: task.status,
        priority: task.priority,
        due_date: task.due_date || '',
        estimated_hours: task.estimated_hours || 0,
        actual_hours: task.actual_hours || 0,
        progress: task.progress || 0
      });

      // Load recurring task data
      loadRecurringTask(task.id);
    }
  }, [task]);

  const loadRecurringTask = async (taskId: string) => {
    try {
      const recurring = await todoService.getRecurringTask(taskId);
      setRecurringTask(recurring);
    } catch (err) {
      // Task is not recurring, that's OK
      setRecurringTask(null);
    }
  };

  if (!task) return null;

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({
      title: task.title,
      description: task.description || '',
      expected_outcome: (task as any).expected_outcome || '',
      status: task.status,
      priority: task.priority,
      due_date: task.due_date || '',
      estimated_hours: task.estimated_hours || 0,
      actual_hours: task.actual_hours || 0,
      progress: task.progress || 0
    });
  };

  const handleSave = () => {
    const updates: UpdateTaskDTO = {
      title: editForm.title,
      description: editForm.description,
      status: editForm.status,
      priority: editForm.priority,
      due_date: editForm.due_date || null,
      estimated_hours: editForm.estimated_hours || null,
      actual_hours: editForm.actual_hours || 0,
      progress: editForm.progress || 0
    };

    // Add expected_outcome to updates if it exists
    if ('expected_outcome' in editForm) {
      (updates as any).expected_outcome = editForm.expected_outcome;
    }

    onUpdate(task.id, updates);
    setIsEditing(false);
  };

  const handleRecurringSave = async (recurringData: CreateRecurringTaskDTO | UpdateRecurringTaskDTO) => {
    if (!task) return;

    try {
      if (recurringTask) {
        // Update existing recurring task
        await todoService.updateRecurringTask(task.id, recurringData as UpdateRecurringTaskDTO);
      } else {
        // Create new recurring task
        await todoService.createRecurringTask(task.id, recurringData as CreateRecurringTaskDTO);
      }

      setShowRecurringDialog(false);
      await loadRecurringTask(task.id);
    } catch (err) {
      console.error('Error saving recurring task:', err);
    }
  };


  const getPriorityColor = (priority: Task['priority']) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      default: return 'default';
    }
  };

  const getStatusColor = (status: Task['status']) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'warning';
      case 'in_review': return 'secondary';
      case 'blocked': return 'error';
      case 'cancelled': return 'default';
      default: return 'info';
    }
  };

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return format(date, 'MMM dd, yyyy at HH:mm');
    } catch {
      return 'Invalid date';
    }
  };

  const isOverdue = task.due_date && new Date(task.due_date) < new Date() && task.status !== 'completed';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: { minHeight: '600px' }
      }}
    >
      <DialogTitle style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: 8 }}>
        <div>
          {isEditing ? (
            <TextField
              value={editForm.title || ''}
              onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
              variant="standard"
              style={{ fontSize: '1.25rem', fontWeight: 500 }}
              fullWidth
            />
          ) : (
            <Typography variant="h6" component="h2">
              {task.title}
            </Typography>
          )}
        </div>
        <Stack direction="row" spacing={1}>
          {!isEditing ? (
            <IconButton onClick={handleEdit} size="small">
              <EditIcon />
            </IconButton>
          ) : (
            <>
              <IconButton onClick={handleSave} size="small" color="primary">
                <SaveIcon />
              </IconButton>
              <IconButton onClick={handleCancel} size="small">
                <CancelIcon />
              </IconButton>
            </>
          )}
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent style={{ paddingBottom: 24 }}>
        <Stack spacing={3}>
          {/* Status and Priority */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>
                <FlagIcon fontSize="small" style={{ marginRight: 8, verticalAlign: 'middle' }} />
                Status
              </Typography>
              {isEditing ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={editForm.status || ''}
                    onChange={(e) => setEditForm({ ...editForm, status: e.target.value as Task['status'] })}
                  >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="in_progress">In Progress</MenuItem>
                    <MenuItem value="in_review">In Review</MenuItem>
                    <MenuItem value="blocked">Blocked</MenuItem>
                    <MenuItem value="completed">Completed</MenuItem>
                    <MenuItem value="cancelled">Cancelled</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <Chip
                  label={task.status.replace('_', ' ')}
                  color={getStatusColor(task.status)}
                  variant="outlined"
                />
              )}
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>
                <FlagIcon fontSize="small" style={{ marginRight: 8, verticalAlign: 'middle' }} />
                Priority
              </Typography>
              {isEditing ? (
                <FormControl fullWidth size="small">
                  <Select
                    value={editForm.priority || ''}
                    onChange={(e) => setEditForm({ ...editForm, priority: e.target.value as Task['priority'] })}
                  >
                    <MenuItem value="low">Low</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="urgent">Urgent</MenuItem>
                  </Select>
                </FormControl>
              ) : (
                <Chip
                  label={task.priority}
                  color={getPriorityColor(task.priority)}
                  variant="outlined"
                />
              )}
            </Grid>

            {/* Assigned Teammate */}
            {task.assigned_teammate_name && (
              <Grid item xs={6}>
                <Typography variant="subtitle2" gutterBottom>
                  <PersonIcon fontSize="small" style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  Assigned Teammate
                </Typography>
                <Chip
                  label={task.assigned_teammate_name}
                  color="primary"
                  variant="outlined"
                  icon={<PersonIcon />}
                />
              </Grid>
            )}
          </Grid>

          {/* Due Date Warning */}
          {isOverdue && (
            <Alert severity="warning">
              This task is overdue! Due date was {formatDistanceToNow(new Date(task.due_date!))} ago.
            </Alert>
          )}

          {/* Dates and Progress */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>
                <CalendarIcon fontSize="small" style={{ marginRight: 8, verticalAlign: 'middle' }} />
                Due Date
              </Typography>
              {isEditing ? (
                <TextField
                  type="datetime-local"
                  value={editForm.due_date ? new Date(editForm.due_date).toISOString().slice(0, 16) : ''}
                  onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value ? new Date(e.target.value).toISOString() : '' })}
                  size="small"
                  fullWidth
                />
              ) : (
                <Typography color={isOverdue ? 'error' : 'text.secondary'}>
                  {task.due_date ? formatDate(task.due_date) : 'No due date set'}
                </Typography>
              )}
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>
                Progress
              </Typography>
              {isEditing ? (
                <TextField
                  type="number"
                  value={editForm.progress || 0}
                  onChange={(e) => setEditForm({ ...editForm, progress: Math.min(100, Math.max(0, Number(e.target.value))) })}
                  size="small"
                  fullWidth
                  InputProps={{ inputProps: { min: 0, max: 100 } }}
                  helperText="0-100%"
                />
              ) : (
                <div>
                  <LinearProgress
                    variant="determinate"
                    value={task.progress || 0}
                    style={{ height: 8, borderRadius: 4, marginBottom: 4 }}
                  />
                  <Typography variant="caption" color="text.secondary">
                    {task.progress || 0}% complete
                  </Typography>
                </div>
              )}
            </Grid>
          </Grid>

          {/* Time Tracking */}
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>
                Estimated Hours
              </Typography>
              {isEditing ? (
                <TextField
                  type="number"
                  value={editForm.estimated_hours || ''}
                  onChange={(e) => setEditForm({ ...editForm, estimated_hours: Number(e.target.value) || 0 })}
                  size="small"
                  fullWidth
                  InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                />
              ) : (
                <Typography color="text.secondary">
                  {task.estimated_hours ? `${task.estimated_hours} hours` : 'Not estimated'}
                </Typography>
              )}
            </Grid>
            <Grid item xs={6}>
              <Typography variant="subtitle2" gutterBottom>
                Actual Hours
              </Typography>
              {isEditing ? (
                <TextField
                  type="number"
                  value={editForm.actual_hours || ''}
                  onChange={(e) => setEditForm({ ...editForm, actual_hours: Number(e.target.value) || 0 })}
                  size="small"
                  fullWidth
                  InputProps={{ inputProps: { min: 0, step: 0.5 } }}
                />
              ) : (
                <Typography color="text.secondary">
                  {task.actual_hours ? `${task.actual_hours} hours` : 'No time logged'}
                </Typography>
              )}
            </Grid>
          </Grid>

          <Divider />

          {/* Description */}
          <div>
            <Typography variant="subtitle2" gutterBottom>
              <DescriptionIcon fontSize="small" style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Description
            </Typography>
            {isEditing ? (
              <TextField
                multiline
                rows={4}
                value={editForm.description || ''}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                placeholder="Enter task description..."
                fullWidth
              />
            ) : (
              <Typography color="text.secondary" style={{ whiteSpace: 'pre-wrap' }}>
                {task.description || 'No description provided'}
              </Typography>
            )}
          </div>

          {/* Expected Outcome */}
          <div>
            <Typography variant="subtitle2" gutterBottom>
              <AssignmentIcon fontSize="small" style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Expected Outcome
            </Typography>
            {isEditing ? (
              <TextField
                multiline
                rows={3}
                value={editForm.expected_outcome || ''}
                onChange={(e) => setEditForm({ ...editForm, expected_outcome: e.target.value })}
                placeholder="What is the expected deliverable or outcome?"
                fullWidth
              />
            ) : (
              <Typography color="text.secondary" style={{ whiteSpace: 'pre-wrap' }}>
                {(task as any).expected_outcome || 'No expected outcome defined'}
              </Typography>
            )}
          </div>

          {/* Work Output */}
          {(task.work_output || task.assigned_teammate_name) && (
            <div>
              <Typography variant="subtitle2" gutterBottom>
                <WorkIcon fontSize="small" style={{ marginRight: 8, verticalAlign: 'middle' }} />
                Work Output
              </Typography>
              {task.work_output ? (
                <div>
                  <Paper
                    style={{
                      padding: 24,
                      backgroundColor: '#fff',
                      border: '1px solid rgba(0,0,0,0.12)',
                      borderRadius: 8
                    }}
                  >
                    {(task.work_output_type === 'html' ||
                      (task.work_output && (
                        task.work_output.includes('<!--HTML-->') ||
                        task.work_output.includes('<div') ||
                        task.work_output.includes('<h1') ||
                        task.work_output.includes('<p>')
                      ))) ? (
                      <Typography
                        variant="body2"
                        style={{
                          color: 'rgba(0,0,0,0.87)',
                          lineHeight: 1.6
                        }}
                        dangerouslySetInnerHTML={{
                          __html: task.work_output
                            ?.replace(/<!--HTML-->/g, '')
                            ?.replace(/color:\s*#333333/g, 'color: rgba(255, 255, 255, 0.87)')
                            ?.replace(/color:\s*#2c3e50/g, 'color: rgba(255, 255, 255, 0.87)')
                            ?.replace(/color:\s*#34495e/g, 'color: rgba(255, 255, 255, 0.7)')
                            ?.replace(/border:\s*1px solid #ddd/g, 'border: 1px solid rgba(255, 255, 255, 0.23)')
                            ?.replace(/background-color:\s*#3498db/g, 'background-color: rgba(52, 152, 219, 0.7)')
                            ?.replace(/background-color:\s*#f8f9fa/g, 'background-color: rgba(255, 255, 255, 0.05)')
                            ?.replace(/background-color:\s*#ecf0f1/g, 'background-color: rgba(255, 255, 255, 0.08)')
                            ?.replace(/border-left:\s*4px solid #3498db/g, 'border-left: 4px solid rgba(52, 152, 219, 0.7)') || ''
                        }}
                      />
                    ) : task.work_output_type === 'markdown' ? (
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          h1: ({ children }) => <Typography variant="h4" gutterBottom style={{ color: 'rgba(0,0,0,0.87)', fontWeight: 'bold', marginTop: 16, marginBottom: 8 }}>{children}</Typography>,
                          h2: ({ children }) => <Typography variant="h5" gutterBottom style={{ color: 'rgba(0,0,0,0.87)', fontWeight: 'bold', marginTop: 16, marginBottom: 8 }}>{children}</Typography>,
                          h3: ({ children }) => <Typography variant="h6" gutterBottom style={{ color: 'rgba(0,0,0,0.87)', fontWeight: 'bold', marginTop: 12, marginBottom: 4 }}>{children}</Typography>,
                          h4: ({ children }) => <Typography variant="subtitle1" gutterBottom style={{ color: 'rgba(0,0,0,0.87)', fontWeight: 'bold', marginTop: 12, marginBottom: 4 }}>{children}</Typography>,
                          h5: ({ children }) => <Typography variant="subtitle2" gutterBottom style={{ color: 'rgba(0,0,0,0.87)', fontWeight: 'bold', marginTop: 8, marginBottom: 4 }}>{children}</Typography>,
                          h6: ({ children }) => <Typography variant="subtitle2" gutterBottom style={{ color: 'rgba(0,0,0,0.87)', fontWeight: 'bold', marginTop: 8, marginBottom: 4 }}>{children}</Typography>,
                          p: ({ children }) => <Typography variant="body2" paragraph style={{ color: 'rgba(0,0,0,0.87)', lineHeight: 1.6, marginBottom: 8 }}>{children}</Typography>,
                          ul: ({ children }) => <ul style={{ color: 'rgba(0,0,0,0.87)', paddingLeft: 16, marginBottom: 8 }}>{children}</ul>,
                          ol: ({ children }) => <ol style={{ color: 'rgba(0,0,0,0.87)', paddingLeft: 16, marginBottom: 8 }}>{children}</ol>,
                          li: ({ children }) => <li style={{ color: 'rgba(0,0,0,0.87)', marginBottom: 4 }}>{children}</li>,
                          strong: ({ children }) => <Typography component="strong" style={{ color: 'rgba(0,0,0,0.87)', fontWeight: 'bold' }}>{children}</Typography>,
                          em: ({ children }) => <Typography component="em" style={{ color: 'rgba(0,0,0,0.87)', fontStyle: 'italic' }}>{children}</Typography>,
                          code: ({ children }) => (
                            <Typography
                              component="code"
                              style={{
                                color: '#1a3f7a',
                                backgroundColor: '#e0e0e0',
                                paddingLeft: 4,
                                paddingRight: 4,
                                paddingTop: 2,
                                paddingBottom: 2,
                                borderRadius: 4,
                                fontFamily: 'monospace',
                                fontSize: '0.875em',
                                fontWeight: 600
                              }}
                            >
                              {children}
                            </Typography>
                          ),
                          pre: ({ children }) => (
                            <Paper
                              style={{
                                padding: 16,
                                backgroundColor: '#e0e0e0',
                                borderRadius: 4,
                                overflow: 'auto',
                                marginBottom: 8,
                                width: '100%'
                              }}
                            >
                              <Typography
                                component="pre"
                                style={{
                                  color: 'rgba(0,0,0,0.87)',
                                  fontFamily: 'monospace',
                                  fontSize: '0.875rem',
                                  margin: 0,
                                  whiteSpace: 'pre-wrap',
                                  width: '100%',
                                  display: 'block'
                                }}
                              >
                                {children}
                              </Typography>
                            </Paper>
                          ),
                          blockquote: ({ children }) => (
                            <Paper
                              style={{
                                padding: 16,
                                backgroundColor: 'rgba(50, 91, 168, 0.05)',
                                borderLeft: '4px solid #325BA8',
                                borderRadius: 4,
                                marginBottom: 8
                              }}
                            >
                              <Typography style={{ color: 'rgba(0,0,0,0.87)', fontStyle: 'italic' }}>
                                {children}
                              </Typography>
                            </Paper>
                          ),
                          hr: () => <Divider style={{ marginTop: 16, marginBottom: 16 }} />,
                          a: ({ href, children }) => (
                            <Typography
                              component="a"
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              style={{
                                color: '#325BA8',
                                textDecoration: 'underline'
                              }}
                            >
                              {children}
                            </Typography>
                          )
                        }}
                      >
                        {task.work_output}
                      </ReactMarkdown>
                    ) : (
                      <Typography
                        variant="body2"
                        style={{
                          color: 'rgba(0,0,0,0.87)',
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.6
                        }}
                      >
                        {task.work_output}
                      </Typography>
                    )}
                  </Paper>
                  {task.work_output_generated_at && (
                    <Typography variant="caption" color="text.secondary" style={{ marginTop: 8, display: 'block' }}>
                      Generated: {formatDate(task.work_output_generated_at)}
                      {task.assigned_teammate_name && ` by ${task.assigned_teammate_name}`}
                    </Typography>
                  )}

                  {/* Citations from AI completion */}
                  {task.ai_completion_data?.citations && task.ai_completion_data.citations.length > 0 && (
                    <div style={{ marginTop: 16 }}>
                      <CitationsDropdown
                        citations={task.ai_completion_data.citations}
                        defaultExpanded={false}
                      />
                    </div>
                  )}
                </div>
              ) : task.assigned_teammate_name ? (
                <Typography color="text.secondary" style={{ fontStyle: 'italic' }}>
                  Assigned to {task.assigned_teammate_name} - work output will be generated automatically
                </Typography>
              ) : null}
            </div>
          )}

          <Divider />

          {/* Assignees */}
          {task.assignee_ids && task.assignee_ids.length > 0 && (
            <div>
              <Typography variant="subtitle2" gutterBottom>
                <PersonIcon fontSize="small" style={{ marginRight: 8, verticalAlign: 'middle' }} />
                Assignees
              </Typography>
              <AvatarGroup max={4}>
                {task.assignee_ids.map((id) => (
                  <Avatar key={id} style={{ width: 32, height: 32 }}>
                    {id[0].toUpperCase()}
                  </Avatar>
                ))}
              </AvatarGroup>
            </div>
          )}

          {/* Labels */}
          {task.labels && task.labels.length > 0 && (
            <div>
              <Typography variant="subtitle2" gutterBottom>
                Labels
              </Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap">
                {task.labels.map((label) => (
                  <Chip
                    key={label}
                    label={label}
                    variant="outlined"
                    size="small"
                  />
                ))}
              </Stack>
            </div>
          )}

          {/* Attachments and Comments */}
          <Grid container spacing={2}>
            {task.attachments && task.attachments.length > 0 && (
              <Grid item xs={6}>
                <Typography variant="subtitle2" gutterBottom>
                  <AttachmentIcon fontSize="small" style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  Attachments ({task.attachments.length})
                </Typography>
                <Stack spacing={1}>
                  {task.attachments.map((attachment) => (
                    <Paper key={attachment.id} style={{ padding: 8, backgroundColor: '#fafafa' }}>
                      <Typography variant="caption" noWrap>
                        {attachment.file_name}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Grid>
            )}
            {task.comments && task.comments.length > 0 && (
              <Grid item xs={6}>
                <Typography variant="subtitle2" gutterBottom>
                  <CommentIcon fontSize="small" style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  Recent Comments ({task.comments.length})
                </Typography>
                <Stack spacing={1}>
                  {task.comments.slice(-3).map((comment) => (
                    <Paper key={comment.id} style={{ padding: 8, backgroundColor: '#fafafa' }}>
                      <Typography variant="caption" color="text.secondary">
                        {comment.userName} • {formatDistanceToNow(new Date(comment.createdAt))} ago
                      </Typography>
                      <Typography variant="body2" style={{ marginTop: 4 }}>
                        {comment.text}
                      </Typography>
                    </Paper>
                  ))}
                </Stack>
              </Grid>
            )}
          </Grid>

          <Divider />

          {/* Metadata */}
          <div>
            <Typography variant="subtitle2" gutterBottom>
              <HistoryIcon fontSize="small" style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Task Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Created: {formatDate(task.created_at)}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary">
                  Last Updated: {formatDate(task.updated_at)}
                </Typography>
              </Grid>
              {task.completed_at && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Completed: {formatDate(task.completed_at)}
                  </Typography>
                </Grid>
              )}
            </Grid>
          </div>
        </Stack>
      </DialogContent>

      <DialogActions style={{ paddingLeft: 24, paddingRight: 24, paddingBottom: 16, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, flex: 1 }}>
          {/* Stage 4 Feature Buttons */}
          <Button
            startIcon={<RepeatIcon />}
            onClick={() => setShowRecurringDialog(true)}
            variant={recurringTask ? 'contained' : 'outlined'}
            color={recurringTask ? 'success' : 'primary'}
            size="small"
          >
            {recurringTask ? 'Edit Recurring' : 'Make Recurring'}
          </Button>

          <Button
            startIcon={<HistoryIcon />}
            onClick={() => setShowHistoryDialog(true)}
            variant="outlined"
            size="small"
          >
            View History
          </Button>

          <Button
            startIcon={<AlarmIcon />}
            onClick={() => setShowReminderDialog(true)}
            variant="outlined"
            size="small"
          >
            Reminders
          </Button>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <Button onClick={onClose}>
            Close
          </Button>
          {!isEditing && (
            <Button variant="contained" onClick={handleEdit}>
              Edit Task
            </Button>
          )}
        </div>
      </DialogActions>

      {/* Stage 4 Feature Dialogs */}
      <RecurringTaskDialog
        open={showRecurringDialog}
        onClose={() => setShowRecurringDialog(false)}
        onSave={handleRecurringSave}
        taskId={task.id}
        taskTitle={task.title}
        existingRecurring={recurringTask}
      />

      <TaskHistoryDialog
        open={showHistoryDialog}
        onClose={() => setShowHistoryDialog(false)}
        taskId={task.id}
        taskTitle={task.title}
      />

      <ReminderListDialog
        open={showReminderDialog}
        onClose={() => setShowReminderDialog(false)}
        taskId={task.id}
      />
    </Dialog>
  );
};

export default TaskDetailModal;
